import { useMemo } from "react";
import { SUPPORTED_PAYOUT_COUNTRY_CODES } from "../data/payoutCoverage";

/**
 * Narrows two country sources down to Blupay's supported payout markets:
 *
 * - `corridorCountries`: from useCorridor().countries (Zuba corridor map —
 *   drives which country codes have channel/field definitions at all)
 * - `geoCountries`: from useCountries() (name/dial-code metadata used to
 *   render the picker)
 *
 * A country only ends up in the dropdown if it's present in BOTH sources
 * AND listed in PAYOUT_COVERAGE — so a Zuba-supported country we haven't
 * turned on (or a geo country Zuba doesn't know) is excluded either way.
 */
export function useSupportedRecipientCountries(
  corridorCountries,
  geoCountries
) {
  const supportedCorridorCountries = useMemo(
    () =>
      (corridorCountries ?? []).filter((c) =>
        SUPPORTED_PAYOUT_COUNTRY_CODES.includes(
          c.code?.toUpperCase()
        )
      ),
    [corridorCountries]
  );

  const supportedCodeSet = useMemo(
    () =>
      new Set(
        supportedCorridorCountries.map((c) =>
          c.code.toUpperCase()
        )
      ),
    [supportedCorridorCountries]
  );

  const countryOptions = useMemo(
    () =>
      (geoCountries ?? [])
        .filter((c) =>
          supportedCodeSet.has(
            c.code?.toUpperCase()
          )
        )
        .map((c) => ({
          value: c.code.toUpperCase(),
          label: c.name,
        })),
    [geoCountries, supportedCodeSet]
  );

  return {
    supportedCorridorCountries,
    countryOptions,
  };
}